import React, { FC, useContext, useState } from 'react';
import { useToast } from '@/hooks';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { ListAndDetailsSubLayoutContext } from '@layouts/ListAndDetailsSubLayout';
import Button from '@/components/DesignLibrary/Button';
import { Input, TextArea } from '@/components/DesignLibrary/Atoms';
import Spacer from '@components/Spacer';

import s from './CreateEditGovernmentForm.module.scss';

type Props = {
  onSubmit?: () => Promise<void>;
  data?: {
    name: string;
    description: string | null;
    id: string;
  };
};

const CreateEditGovernmentForm: FC<Props> = ({ onSubmit, data }) => {
  const { reloadListView } = useContext(ListAndDetailsSubLayoutContext);
  const [name, setName] = useState(data?.name || '');
  const [description, setDescription] = useState(data?.description || '');
  const [assetType] = useState<'html' | 'css' | 'js' | 'json'>('html');
  const addToast = useToast();

  const { fetchingState, getData: createDynamicPdfTemplateFn } = useNoodleApi(
    tsClient.createDynamicPdfTemplate,
    {
      toastOnError: true,
      toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Form created successfully!'],
    },
  );

  const { fetchingState: fetchingStateAssets, getData: createDynamicPdfTemplateAssetFn } = useNoodleApi(
    tsClient.createDynamicPdfTemplateAsset,
    {
      toastOnError: true,
      toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Form created successfully!'],
    },
  );

  const { getData: updateDynamicPdfTemplateFn } = useNoodleApi(tsClient.updateDynamicPdfTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Form updated successfully!'],
  });

  const handleUpdate = async (): Promise<void> => {
    try {
      if (data?.id) {
        await updateDynamicPdfTemplateFn(data.id, {
          description,
          name,
        });
        await onSubmit?.();
      }
    } catch (error) {
      addToast(useToast.ToastTypeVariants.ERROR, `Failed to update form. Please try again.`);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (data) {
      await handleUpdate();
      return;
    }
    try {
      if (assetType === 'html') {
        await createDynamicPdfTemplateFn({ category: 'bankruptcy', content: '<p>HTML content</p>', description, name });
      } else if (assetType === 'css') {
        await createDynamicPdfTemplateAssetFn({ assetType: 'css', category: 'bankruptcy', content: '', description, name });
      } else if (assetType === 'js') {
        await createDynamicPdfTemplateAssetFn({ assetType: 'js', category: 'bankruptcy', content: '', description, name });
      } else if (assetType === 'json') {
        await createDynamicPdfTemplateAssetFn({ assetType: 'json', category: 'bankruptcy', content: '', description, name });
      }
      await onSubmit?.();
      reloadListView?.();
    } catch (error) {
      addToast(useToast.ToastTypeVariants.ERROR, `Failed to create form. Please try again.`);
    }
  };

  return (
    <div className={s.padding}>
      <Input
        label="Government Form Name"
        value={name}
        onChange={setName}
        optional={false}
        compact
      />
      <Spacer />
      <TextArea
        label="Description"
        value={description}
        onChange={setDescription}
        optional
        compact
      />
      <Spacer />
      {/* @todo These radio buttons may be required later */}
      {/* <RadioButtons
        options={[
          { key: 'html', label: 'HTML' },
          { key: 'css', label: 'CSS' },
          { key: 'js', label: 'JavaScript' },
          { key: 'json', label: 'JSON' },
        ]}
        label="Asset Type"
        name="assetType"
        onChange={(e) => setAssetType(e.target.value as 'html' | 'css' | 'js' | 'json')}
        value={assetType}
        required
      /> */}
      <Spacer />
      <Button size="md" variant="primary" fullWidth onClick={handleSave} disabled={!name} loading={fetchingState.isFetching || fetchingStateAssets.isFetching}>
        {data ? 'Update Form' : 'Create Form'}
      </Button>
    </div>
  );
};

export default CreateEditGovernmentForm;
